import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { supabase } from '@/integrations/supabase/client';
import { ADMIN_NOTIFICATION_EMAILS } from '@/lib/notifications';

type Msg = { role: 'user' | 'assistant'; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatbot`;

const WELCOME_MSG: Msg = {
  role: 'assistant',
  content: 'Ahoj! 👋 Jsem Arty, Tomův asistent pro **Tom Art Mask**. Zajímá vás custom design brankářské masky? Rád vám poradím!\n\n_Konverzace je zpracovávána dle [Zásad ochrany osobních údajů](/privacy)._',
};

async function streamChat({
  messages,
  onDelta,
  onDone,
  onError,
}: {
  messages: Msg[];
  onDelta: (t: string) => void;
  onDone: () => void;
  onError: (msg: string) => void;
}) {
  const resp = await fetch(CHAT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ messages }),
  });

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    onError(data.error || 'Něco se pokazilo, zkuste to znovu.');
    return;
  }
  if (!resp.body) { onError('Prázdná odpověď'); return; }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let done = false;

  while (!done) {
    const { done: d, value } = await reader.read();
    if (d) break;
    buf += decoder.decode(value, { stream: true });

    let nl: number;
    while ((nl = buf.indexOf('\n')) !== -1) {
      let line = buf.slice(0, nl);
      buf = buf.slice(nl + 1);
      if (line.endsWith('\r')) line = line.slice(0, -1);
      if (line.startsWith(':') || line.trim() === '') continue;
      if (!line.startsWith('data: ')) continue;
      const json = line.slice(6).trim();
      if (json === '[DONE]') { done = true; break; }
      try {
        const p = JSON.parse(json);
        const c = p.choices?.[0]?.delta?.content as string | undefined;
        if (c) onDelta(c);
      } catch {
        buf = line + '\n' + buf;
        break;
      }
    }
  }
  onDone();
}

// Detect contact info in conversation
function extractContact(msgs: Msg[]) {
  const text = msgs.map(m => m.content).join(' ');
  const emailMatch = text.match(/[\w.-]+@[\w.-]+\.\w{2,}/);
  const phoneMatch = text.match(/(?:\+?\d[\d\s\-()]{7,}\d)/);

  // Validate extracted email
  const rawEmail = emailMatch?.[0] || null;
  const emailValid = rawEmail && /^[\w.-]+@[\w.-]+\.\w{2,}$/.test(rawEmail) && rawEmail.length <= 254;
  const email = emailValid ? rawEmail : null;

  // Validate extracted phone (7–20 digits after stripping non-numeric)
  const rawPhone = phoneMatch?.[0] || null;
  const digits = rawPhone?.replace(/\D/g, '') || '';
  const phone = rawPhone && digits.length >= 7 && digits.length <= 20 ? rawPhone.trim().slice(0, 30) : null;

  return { email, phone };
}

const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [pulse, setPulse] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const savedRef = useRef(false);

  // Auto-open after 30s
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasAutoOpened) {
        setOpen(true);
        setHasAutoOpened(true);
      }
    }, 30000);

    // Pulse animation after 10s
    const pulseTimer = setTimeout(() => setPulse(true), 10000);

    return () => { clearTimeout(timer); clearTimeout(pulseTimer); };
  }, [hasAutoOpened]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Save lead when contact detected
  useEffect(() => {
    if (savedRef.current) return;
    const { email, phone } = extractContact(messages);
    if (email || phone) {
      savedRef.current = true;
      const conversation = messages.map(m => `${m.role}: ${m.content}`).join('\n').slice(0, 2000);
      const leadId = crypto.randomUUID();
      supabase.from('chatbot_leads').insert({ id: leadId, email, phone, conversation }).then(({ error }) => {
        if (error) {
          console.error('Failed to save lead:', error.message);
          return;
        }
        // Notify admin about new lead — send to all configured recipients
        ADMIN_NOTIFICATION_EMAILS.forEach((adminEmail) => {
          const slug = adminEmail.split('@')[0].replace(/[^a-z0-9]/gi, '');
          supabase.functions.invoke('send-transactional-email', {
            body: {
              templateName: 'admin-new-chatbot-lead',
              recipientEmail: adminEmail,
              idempotencyKey: `admin-lead-${leadId}-${slug}`,
              templateData: { email: email || '', phone: phone || '', conversation },
            },
          }).catch((err) => console.error(`Lead notification to ${adminEmail} failed:`, err));
        });
      });
    }
  }, [messages]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg: Msg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    let assistantSoFar = '';
    const upsert = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && prev.length > 1 && prev[prev.length - 2]?.role === 'user') {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
        }
        return [...prev, { role: 'assistant', content: assistantSoFar }];
      });
    };

    try {
      await streamChat({
        messages: [...messages, userMsg],
        onDelta: upsert,
        onDone: () => setLoading(false),
        onError: (msg) => {
          setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${msg}` }]);
          setLoading(false);
        },
      });
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Připojení selhalo, zkuste to prosím znovu.' }]);
      setLoading(false);
    }
  }, [input, loading, messages]);

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => { setOpen(true); setHasAutoOpened(true); setPulse(false); }}
            className={`fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-110 transition-transform ${pulse ? 'animate-bounce' : ''}`}
            aria-label="Otevřít chat"
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 left-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <span className="font-semibold text-sm">Arty – Tomův asistent 🎨</span>
              </div>
              <button onClick={() => setOpen(false)} className="hover:opacity-70 transition-opacity">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                      m.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                        : 'bg-muted text-foreground rounded-bl-sm'
                    }`}
                  >
                    {m.role === 'assistant' ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:m-0 [&>p+p]:mt-1">
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      </div>
                    ) : (
                      m.content
                    )}
                  </div>
                </div>
              ))}
              {loading && messages[messages.length - 1]?.role === 'user' && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl rounded-bl-sm px-3 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 p-3 border-t border-border"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Napište zprávu..."
                className="flex-1 bg-muted/50 border border-border rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:opacity-90 disabled:opacity-40 transition-opacity"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
